import { ComponentType, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiLogOut, FiPlus } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import './PublicShell.css';
import '../../styles/public-tokens.css';

type ShellIconProps = {
    'aria-hidden'?: boolean | 'true' | 'false';
};

const ChevronDownIcon = FiChevronDown as unknown as ComponentType<ShellIconProps>;
const LogoutIcon = FiLogOut as unknown as ComponentType<ShellIconProps>;
const PlusIcon = FiPlus as unknown as ComponentType<ShellIconProps>;

type PublicLink = {
    label: string;
    href: string;
    match: (hash: string) => boolean;
};

const primaryLinks: PublicLink[] = [
    { label: '오늘', href: '#/', match: (hash) => hash === '#/' || hash === '' },
    { label: '발견', href: '#/board', match: (hash) => hash.startsWith('#/board') && !hash.includes('tab=PHOTO') },
    { label: '기획', href: '#/collections', match: (hash) => hash.startsWith('#/collections') },
    { label: '사진', href: '#/board?tab=PHOTO', match: (hash) => (hash.startsWith('#/board') && hash.includes('tab=PHOTO')) || hash.startsWith('#/create-photo') },
    { label: '관찰실', href: '#/observation', match: (hash) => hash.startsWith('#/observation') },
    { label: '영상', href: '#/watch', match: (hash) => hash.startsWith('#/watch') },
    { label: '질문 올리기', href: '#/create', match: (hash) => hash === '#/create' || hash.startsWith('#/create?') }
];

const PublicShell = ({ children }: PropsWithChildren) => {
    const location = useLocation();
    const [accountOpen, setAccountOpen] = useState(false);
    const accountRef = useRef<HTMLDivElement>(null);
    const token = localStorage.getItem('token');
    const nickname = localStorage.getItem('nickname') || '내 계정';
    const role = localStorage.getItem('role');
    const currentHash = `#${location.pathname}${location.search}`;

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [currentHash]);

    useEffect(() => {
        if(!accountOpen) {
            return;
        }

        const closeOnOutsidePress = (event: MouseEvent) => {
            if(!accountRef.current?.contains(event.target as Node)) {
                setAccountOpen(false);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if(event.key === 'Escape') {
                setAccountOpen(false);
            }
        };

        document.addEventListener('mousedown', closeOnOutsidePress);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsidePress);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [accountOpen]);

    const logout = () => {
        ['token', 'role', 'nickname', 'userId'].forEach((key) => localStorage.removeItem(key));
        setAccountOpen(false);
        window.location.hash = '#/';
        window.location.reload();
    };

    return (
        <div className="public-app">
            <header className="public-header">
                <a className="public-wordmark" href="#/" aria-label="TruthPick 홈">
                    truth<span>/</span>pick
                </a>

                <nav className="public-desktop-nav" aria-label="주요 메뉴">
                    {primaryLinks.map((link) => (
                        <a
                            key={link.href}
                            href={link.href}
                            className={link.match(currentHash) ? 'active' : ''}
                            aria-current={link.match(currentHash) ? 'page' : undefined}
                        >
                            {link.label}
                        </a>
                    ))}
                    {role === 'ADMIN' && <a href="#/admin">관리자</a>}
                </nav>

                <div className="public-account" ref={accountRef}>
                    {!token ? (
                        <a className="public-account-link" href="#/login">로그인</a>
                    ) : (
                        <>
                            <button
                                className="public-account-trigger"
                                type="button"
                                aria-haspopup="menu"
                                aria-expanded={accountOpen}
                                onClick={() => setAccountOpen((open) => !open)}
                            >
                                <span>{nickname}</span>
                                <ChevronDownIcon aria-hidden="true" />
                            </button>
                            {accountOpen && (
                                <nav className="public-account-menu" aria-label="계정 메뉴">
                                    {role === 'ADMIN' && <a href="#/admin">관리자</a>}
                                    <a href="#/board">내 활동</a>
                                    <button type="button" onClick={logout}>
                                        <LogoutIcon aria-hidden="true" />
                                        로그아웃
                                    </button>
                                </nav>
                            )}
                        </>
                    )}
                </div>
            </header>

            <div className="public-content">{children}</div>

            <footer className="public-footer"><div className="public-footer-inner">
                <div><a className="public-footer-brand" href="#/">truth<span>/</span>pick</a><p>보이는 말 너머의 근거를 함께 찾아갑니다.</p></div>
                <nav aria-label="사이트 안내"><a href="#/mirror">이번 주 질문</a><a href="#/lens">3문장 읽기</a><a href="#/board">커뮤니티</a><a href="#/collections">주제 탐사</a><a href="#/board?tab=PHOTO">사진</a><a href="#/observation">사진 관찰실</a><a href="#/watch">영상 이야기</a><a href="#/create">질문 올리기</a><a href="#/privacy">개인정보처리방침</a></nav>
                <div className="public-footer-bottom"><span>© {new Date().getFullYear()} TruthPick</span><a href="https://www.youtube.com/@%EC%9D%B4%EC%83%81%ED%95%9C%EB%82%98%EB%9D%BC%EC%9D%98%EC%8B%A0%EA%B8%B0%ED%95%9C%EA%B0%9C" target="_blank" rel="noopener noreferrer">운영자 YouTube ↗</a></div>
            </div></footer>

            <nav className="public-mobile-nav" aria-label="모바일 메뉴">
                {primaryLinks.filter((link) => link.label !== '관찰실').map((link) => (
                    <a
                        key={link.href}
                        href={link.href}
                        className={`${link.match(currentHash) || (link.label === '사진' && currentHash.startsWith('#/observation')) ? 'active' : ''} ${link.label === '질문 올리기' ? 'create' : ''}`.trim()}
                        aria-current={link.match(currentHash) || (link.label === '사진' && currentHash.startsWith('#/observation')) ? 'page' : undefined}
                        aria-label={link.label}
                    >
                        {link.label === '질문 올리기' ? <PlusIcon aria-hidden="true" /> : link.label}
                    </a>
                ))}
                <a href={token ? '#/board' : '#/login'}>{token ? '내 활동' : '로그인'}</a>
            </nav>
        </div>
    );
};

export default PublicShell;
